import {
  type DifferentialPrice,
  type Frequency,
  type PeriodUnit,
  type Price,
} from '@bbkPricingPage/types/pricing-table-types';

export const filterPricesByVariant = (prices: Price[], variantId?: string) => {
  // Filter by variantId (if provided), else fallback to default (null/undefined)
  if (!variantId) return prices.filter((price) => !price.price_variant_id);
  const matchingPrices = prices.filter(
    (price) => price.price_variant_id === variantId
  );

  return matchingPrices.length > 0
    ? matchingPrices
    : prices.filter((price) => !price.price_variant_id);
};

export const applyDifferentialPricing = (
  basePrice: Price,
  differential: DifferentialPrice
): Price => {
  const overridePeriod = Boolean(
    differential.parent_period && differential.parent_period_unit
  );
  return {
    ...basePrice,
    amount: differential.amount,
    tiers: differential.tiers,
    period: overridePeriod
      ? (differential.parent_period as number)
      : basePrice.period,
    period_unit: overridePeriod
      ? (differential.parent_period_unit as PeriodUnit)
      : basePrice.period_unit,
  };
};

export const findMatchingFrequency = (
  price: Price,
  frequencies: Frequency[]
) => {
  return frequencies.find(
    (frequency: Frequency) =>
      frequency.period === price.period &&
      frequency.period_unit === price.period_unit
  ) as Frequency;
};

export const findMatchingDifferentialPrice = (
  differentialPrices: DifferentialPrice[] | undefined,
  itemId: string,
  parentPeriod?: number | null,
  parentPeriodUnit?: PeriodUnit | null
) => {
  // Match by itemId, parentPeriod, and parentPeriodUnit
  if (!!parentPeriod && !!parentPeriodUnit) {
    const differentialPrice = differentialPrices?.find(
      (dp) =>
        dp.billing_system_item_id === itemId &&
        dp.parent_period === parentPeriod &&
        dp.parent_period_unit === parentPeriodUnit
    );
    if (differentialPrice) return differentialPrice;
  }

  // If no exact match, try to find a default price without parentPeriod and parentPeriodUnit
  return differentialPrices?.find(
    (dp) =>
      dp.billing_system_item_id === itemId &&
      !dp.parent_period &&
      !dp.parent_period_unit
  );
};

export const getPriceWithDifferentialPrices = (
  price: Price,
  itemId: string
): Price => {
  const differentialPrice = findMatchingDifferentialPrice(
    price.differential_prices,
    itemId
  );
  return differentialPrice
    ? applyDifferentialPricing(price, differentialPrice)
    : price;
};
